import { useCallback, useEffect, useRef, useState } from 'react';

import { UploadSemaphore } from '@/lib/upload';

// Staging pipeline for composer attachments: admits picked / dropped / pasted
// files up to a cap, rejects the ones the caller won't upload, runs the
// caller's upload (presign → PUT, optionally finalize) with per-file progress,
// and owns each file's blob: preview URL and AbortController until the entry
// is removed, cleared, or handed off.

const UPLOAD_CONCURRENCY = 3;

export type StagedUploadState<R> =
  | { readonly status: 'uploading'; readonly progress: number }
  | { readonly status: 'ready'; readonly result: R }
  | { readonly status: 'error'; readonly errorMessage: string };

export type StagedUpload<R> = {
  readonly id: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  /** blob: preview for images; null for other types. */
  readonly previewUrl: string | null;
  readonly state: StagedUploadState<R>;
};

export type StagedUploadIo = {
  readonly onProgress: (pct: number) => void;
  readonly signal: AbortSignal;
};

export type UseStagedUploadsOptions<R> = {
  readonly maxCount: number;
  /** Message for a file the pipeline won't upload (type, size), or null to admit it. */
  readonly rejectReason: (file: File) => string | null;
  readonly upload: (file: File, io: StagedUploadIo) => Promise<R>;
  readonly uploadFailedMessage: string;
};

export type StagedUploads<R> = {
  readonly staged: readonly StagedUpload<R>[];
  readonly uploading: boolean;
  /** Stages as many files as fit under `maxCount`; returns how many didn't fit. */
  readonly add: (files: readonly File[]) => { readonly overflow: number };
  readonly remove: (id: string) => void;
  /** Aborts every in-flight upload and revokes every preview. */
  readonly clear: () => void;
  /** Drops ready entries WITHOUT revoking their previews — the caller now owns
   *  those blob: URLs (e.g. an optimistic message still renders them). */
  readonly handOffReady: () => void;
};

type Resources = { readonly controller: AbortController; readonly previewUrl: string | null };

export function useStagedUploads<R>(options: UseStagedUploadsOptions<R>): StagedUploads<R> {
  const [staged, setStaged] = useState<StagedUpload<R>[]>([]);
  // Synchronous registry of everything staged. Its size is the cap check, so
  // rapid successive adds (several before a re-render) can't collectively
  // exceed `maxCount` the way reading `staged.length` from a stale closure would.
  const resourcesRef = useRef(new Map<string, Resources>());
  const semaphoreRef = useRef(new UploadSemaphore(UPLOAD_CONCURRENCY));
  const nextIdRef = useRef(0);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const setState = useCallback((id: string, state: StagedUploadState<R>): void => {
    setStaged((prev) => prev.map((s) => (s.id === id ? { ...s, state } : s)));
  }, []);

  const release = useCallback((id: string): void => {
    const res = resourcesRef.current.get(id);
    if (res === undefined) return;
    res.controller.abort();
    if (res.previewUrl !== null) URL.revokeObjectURL(res.previewUrl);
    resourcesRef.current.delete(id);
  }, []);

  const runUpload = useCallback(
    async (id: string, file: File, signal: AbortSignal): Promise<void> => {
      const { upload, uploadFailedMessage } = optionsRef.current;
      try {
        const result = await semaphoreRef.current.run(() => {
          if (signal.aborted) throw signal.reason;
          return upload(file, {
            signal,
            onProgress: (progress) => {
              if (!signal.aborted) setState(id, { status: 'uploading', progress });
            },
          });
        });
        if (!signal.aborted) setState(id, { status: 'ready', result });
      } catch {
        // A removed / cleared entry is already gone from state; anything else
        // is a real failure the member sees on the entry itself.
        if (!signal.aborted) setState(id, { status: 'error', errorMessage: uploadFailedMessage });
      }
    },
    [setState],
  );

  const add = useCallback(
    (files: readonly File[]): { overflow: number } => {
      const { maxCount, rejectReason } = optionsRef.current;
      const admitted: Array<{ entry: StagedUpload<R>; file: File; signal: AbortSignal }> = [];
      let overflow = 0;
      for (const file of files) {
        if (resourcesRef.current.size >= maxCount) {
          overflow += 1;
          continue;
        }
        nextIdRef.current += 1;
        const id = `staged-${nextIdRef.current}`;
        const controller = new AbortController();
        const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
        resourcesRef.current.set(id, { controller, previewUrl });
        const reason = rejectReason(file);
        admitted.push({
          file,
          signal: controller.signal,
          entry: {
            id,
            fileName: file.name,
            contentType: file.type,
            sizeBytes: file.size,
            previewUrl,
            state:
              reason === null
                ? { status: 'uploading', progress: 0 }
                : { status: 'error', errorMessage: reason },
          },
        });
      }
      if (admitted.length > 0) {
        setStaged((prev) => [...prev, ...admitted.map((a) => a.entry)]);
        for (const { entry, file, signal } of admitted) {
          if (entry.state.status === 'uploading') void runUpload(entry.id, file, signal);
        }
      }
      return { overflow };
    },
    [runUpload],
  );

  const remove = useCallback(
    (id: string): void => {
      release(id);
      setStaged((prev) => prev.filter((s) => s.id !== id));
    },
    [release],
  );

  const clear = useCallback((): void => {
    for (const id of [...resourcesRef.current.keys()]) release(id);
    setStaged([]);
  }, [release]);

  const handOffReady = useCallback((): void => {
    setStaged((prev) => prev.filter((s) => s.state.status !== 'ready'));
    for (const s of staged) {
      if (s.state.status === 'ready') resourcesRef.current.delete(s.id);
    }
  }, [staged]);

  // Abandoned drafts: abort uploads still running and free their previews.
  useEffect(() => {
    const resources = resourcesRef.current;
    return () => {
      for (const res of resources.values()) {
        res.controller.abort();
        if (res.previewUrl !== null) URL.revokeObjectURL(res.previewUrl);
      }
      resources.clear();
    };
  }, []);

  return {
    staged,
    uploading: staged.some((s) => s.state.status === 'uploading'),
    add,
    remove,
    clear,
    handOffReady,
  };
}
